/**
 * Dashboard API client.
 *
 * Phase 5-1 ~ 5-5 / Plan §4.1.
 *
 * 提供 D1 / D4 / D6 看板的 3 个 GET 拉数据接口 + 1 个统一导出接口；
 * DTO 与 admin-service `services/dashboard/dashboard.service.ts` 返回类型 1:1 对齐。
 */
import { apiClient } from './client';

// ── DTO 类型 ─────────────────────────────────────────────────────────────────

export type Period = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface NorthStarBucket {
  bucket: string;
  count: number;
}

export interface NorthStarRetention {
  cohort: string;
  d1: number;
  d7: number;
  d30: number;
}

export interface NorthStarDto {
  /** 注册用户总数（全量，不受时间窗影响） */
  totalUsers: number;
  /** 当日新增用户（服务端自然日，不受时间窗影响） */
  newUsersToday: number;
  dau: number;
  wau: number;
  mau: number;
  newUsersSeries: NorthStarBucket[];
  activeUsersSeries: NorthStarBucket[];
  retention: NorthStarRetention[];
  notes: string;
}

export interface NorthStarFilter {
  period?: Period;
  channel?: string;
  startAt: string;
  endAt: string;
}

export interface PrintRegionBucket {
  province: string;
  orderCount: number;
  revenue: number;
}

export interface PrintMonthBucket {
  bucket: string;
  orderCount: number;
  revenue: number;
}

export interface PrintFulfillmentDto {
  orderCount: number;
  totalRevenue: number;
  aov: number;
  refundCount: number;
  refundAmount: number;
  refundRate: number;
  regionDistribution: PrintRegionBucket[];
  monthlySeries: PrintMonthBucket[];
}

export interface PrintFulfillmentFilter {
  startAt: string;
  endAt: string;
  province?: string;
}

export interface ContentStyleBucket {
  style: string;
  workCount: number;
}

export interface ContentMonthlyBucket {
  bucket: string;
  workCount: number;
}

export interface ContentProfileDto {
  workCount: number;
  chapterCount: number;
  finalizationRate: number;
  styleDistribution: ContentStyleBucket[];
  monthlyWorksSeries: ContentMonthlyBucket[];
  notes: string;
}

export interface ContentProfileFilter {
  startAt: string;
  endAt: string;
  style?: string;
}

export type DashboardKind = 'north-star' | 'print' | 'content';

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

// ── 工具：把 filter 对象转 params 字典，去除 undefined/empty ──
function compact(
  obj: Record<string, string | number | undefined>,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') {
      out[k] = v;
    }
  }
  return out;
}

// ── 三个 GET ────────────────────────────────────────────────────────────────

export async function getNorthStar(filter: NorthStarFilter): Promise<NorthStarDto> {
  const resp = await apiClient.get<ServiceResponse<NorthStarDto>>('/dashboard/north-star', {
    params: compact({
      period: filter.period,
      channel: filter.channel,
      startAt: filter.startAt,
      endAt: filter.endAt,
    }),
  });
  return resp.data.data;
}

export async function getPrintFulfillment(
  filter: PrintFulfillmentFilter,
): Promise<PrintFulfillmentDto> {
  const resp = await apiClient.get<ServiceResponse<PrintFulfillmentDto>>('/dashboard/print', {
    params: compact({
      startAt: filter.startAt,
      endAt: filter.endAt,
      province: filter.province,
    }),
  });
  return resp.data.data;
}

export async function getContentProfile(
  filter: ContentProfileFilter,
): Promise<ContentProfileDto> {
  const resp = await apiClient.get<ServiceResponse<ContentProfileDto>>('/dashboard/content', {
    params: compact({
      startAt: filter.startAt,
      endAt: filter.endAt,
      style: filter.style,
    }),
  });
  return resp.data.data;
}

export interface CreationFunnelDto {
  workCount: number;
  narratedCount: number;
  dialogueDoneCount: number;
  finalizedCount: number;
  unlockedCount: number;
  printedCount: number;
  dialogueCount: number;
  avgNarrationMinutes: number;
  pdfWorkCount: number;
  shareCount: number;
  notes: string;
}

export async function getCreationFunnel(
  filter: { startAt: string; endAt: string },
): Promise<CreationFunnelDto> {
  const resp = await apiClient.get<ServiceResponse<CreationFunnelDto>>('/dashboard/creation-funnel', {
    params: compact({ startAt: filter.startAt, endAt: filter.endAt }),
  });
  return resp.data.data;
}

// ── 统一导出 ────────────────────────────────────────────────────────────────

export interface ExportResult {
  /** axios 拿到的 Blob */
  blob: Blob;
  /** 从 Content-Disposition 解析出的文件名，缺省时用默认值 */
  filename: string;
}

export type ExportFilter = NorthStarFilter | PrintFulfillmentFilter | ContentProfileFilter;

function parseFilenameFromCD(cd: string | undefined): string | null {
  if (!cd) return null;
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * 触发 CSV 导出，返回 Blob + 解析后的文件名。
 *
 * UI 端拿到结果后调 `utils/export-watermark.triggerDownload(blob, filename)`
 * 完成浏览器下载。filename 已含服务端水印 hash（hash 是 operatorId 的 sha256 前 8 位）。
 */
export async function exportDashboard(
  kind: DashboardKind,
  filter: ExportFilter,
): Promise<ExportResult> {
  const resp = await apiClient.get<Blob>(`/dashboard/${kind}/export`, {
    responseType: 'blob',
    params: compact({
      format: 'csv',
      startAt: filter.startAt,
      endAt: filter.endAt,
      period: (filter as NorthStarFilter).period,
      channel: (filter as NorthStarFilter).channel,
      province: (filter as PrintFulfillmentFilter).province,
      style: (filter as ContentProfileFilter).style,
    }),
  });
  // axios 把 headers 都小写化
  const cd = (resp.headers as Record<string, string | undefined>)['content-disposition'];
  const parsed = parseFilenameFromCD(cd);
  return {
    blob: resp.data,
    filename: parsed ?? `dashboard-${kind}_${Date.now()}.csv`,
  };
}
